/* HR — Payroll (R5 fully wired)
 *
 *  Endpoints used:
 *    GET    /api/payroll/periods?year=
 *    POST   /api/payroll/periods                       { year, month, pay_date?, note? }                (hr/admin)
 *    PATCH  /api/payroll/periods/:id                   { pay_date?, note? }                              (hr/admin)
 *    DELETE /api/payroll/periods/:id                                                                     (hr/admin, only open & no slips)
 *    POST   /api/payroll/periods/:id/generate                                                            (hr/admin) → 為所有 active 員工建 draft payslips
 *    POST   /api/payroll/periods/:id/calculate                                                           (hr/admin) → 從 attendance_daily 帶入 overtime + recompute gross/net
 *    POST   /api/payroll/periods/:id/lock                                                                (hr/admin) → calculating → locked
 *    POST   /api/payroll/periods/:id/publish                                                             (hr/admin) → payslips draft → published（員工可見）
 *    POST   /api/payroll/periods/:id/pay                { paid_at? }                                     (finance/admin) → locked → paid
 *    GET    /api/payroll/payslips?period_id=&employee_id=&status=&page=
 *    GET    /api/payroll/payslips/me?year=
 *    GET    /api/payroll/payslips/:id
 *    PATCH  /api/payroll/payslips/:id                  (hr/admin, period open/calculating)
 *    POST   /api/payroll/payslips/:id/view                                                               (self) → published → viewed
 *    POST   /api/payroll/payslips/mark-signed             { payslip_ids }                                (hr/admin) → confirming → confirmed（紙本簽收登記）
 *    GET    /api/payroll/recurring-items?employee_id=                                                    (hr/admin) 固定加減項列表
 *    POST   /api/payroll/recurring-items               { employee_id, kind, name, amount, effective_from, effective_to?, remark? }  (hr/admin)
 *    PATCH  /api/payroll/recurring-items/:id           { effective_to?, remark? }                        (hr/admin) 停用＝設 effective_to
 *
 *  狀態機:
 *    Period:  open → calculating → verifying → locked → paid
 *    Payslip: draft → confirming(待簽收) → confirmed(已簽收，HR mark-signed)
 *             （publish 僅轉換 draft/confirming；舊資料的 published → viewed 僅保留顯示）
 */

const PERIOD_STATUS = {
  open: { label: '開放編輯', tone: 'neutral' },
  calculating: { label: '計算中', tone: 'warn' },
  verifying: { label: '簽收中', tone: 'warn' },   // 紙本流程：已確認薪資，列印發放中，待員工簽名回收
  locked: { label: '已鎖定', tone: 'accent' },
  paid: { label: '已發薪', tone: 'accent' },
};

const PAYSLIP_STATUS = {
  draft: { label: '草稿', tone: 'neutral' },
  confirming: { label: '待簽收', tone: 'warn' },
  confirmed: { label: '已簽收', tone: 'success' },
  published: { label: '已公布', tone: 'warn' },
  viewed: { label: '已查看', tone: 'accent' },
};

/* Capabilities used in this module:
 *   payroll.read.all  → 看「薪資期間」分頁（list 所有員工薪資）
 *   payroll.write     → 建期間 / 編 payslip / 計算
 *   payroll.approve   → 鎖定 / 公布
 *   payroll.pay       → 標記發薪
 * 後端 require_role 對應 hr=write+approve, finance=pay, admin=all。 */

/* ── Expressive (GX) skin helpers — plan-expressive-inner-pages.md §2(d). VISUAL ONLY: no amount,
 * status or snapshot logic is touched (SPEC §3, §9 #11). Render-time only; under Bento they return
 * the ORIGINAL object (uiStyle), so Bento output is unchanged. */
// Uppercase / letter-spaced micro label → GX sentence-case 700 label (onV: AA on every tonal surface).
function _pyGxLabel(bento) {
  return uiStyle(bento, { letterSpacing: 0, textTransform: 'none', fontWeight: 700, color: GxT.color.onV });
}
// Small hint / info box → GX tonal s2 box, radius 12, no border.
function _pyGxBox(bento) {
  return uiStyle(bento, { background: GxT.color.s2, borderRadius: GxT.radius.inner, border: 'none' });
}
// Italic serif note text → GX upright (Figtree has no true italic).
function _pyGxUpright(bento) {
  return uiStyle(bento, { fontStyle: 'normal' });
}

function tryParsePayrollJSON(s) {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
}

/* ─────────────────────────  Period form (new period)  ──────── */

function NewPeriodForm({ onCancel, onSaved }) {
  const toast = useToast();
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth() + 1);
  const [payDate, setPayDate] = React.useState('');
  const [note, setNote] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api('POST', '/payroll/periods', {
        year: parseInt(year), month: parseInt(month),
        pay_date: payDate || null,
        note: note.trim() || null,
      });
      toast(`已建立 ${year}年${month}月 薪資期間`, 'ok');
      onSaved();
    } catch (e) { toast('建立失敗：' + (e.detail || ''), 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onCancel}
      title="新增薪資期間"
      subtitle="New Payroll Period"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '建立中…' : '建立'}</Button>
        </>
      }
    >
      <div className={GxCore.cls('hr-form-grid', 'gx-py-form')} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="年" required>
          <input type="number" value={year} onChange={(e) => setYear(e.target.value)} style={inputStyle({ fontFamily: HR.mono, textAlign: 'right' })} />
        </Field>
        <Field label="月" required>
          <input type="number" min={1} max={12} value={month} onChange={(e) => setMonth(e.target.value)} style={inputStyle({ fontFamily: HR.mono, textAlign: 'right' })} />
        </Field>
        <Field label="預定發薪日" span={2}>
          <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} style={inputStyle()} />
        </Field>
        <Field label="備註" span={2}>
          <Textarea value={note} onChange={setNote} rows={2} />
        </Field>
      </div>
    </Modal>
  );
}

/* ─────────────────────────  Pay action modal  ─────────────── */

function PayPeriodModal({ period, onCancel, onSaved }) {
  const toast = useToast();
  const [paidAt, setPaidAt] = React.useState(period.pay_date || todayStr());
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api('POST', `/payroll/periods/${period.id}/pay`, { paid_at: paidAt });
      toast(`已標記 ${period.year}/${period.month} 薪資為已發薪`, 'ok');
      onSaved();
    } catch (e) { toast('發薪失敗：' + (e.detail || ''), 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onCancel}
      title="標記已發薪"
      subtitle={`${period.year}年${period.month}月`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '處理中…' : '確認發薪'}</Button>
        </>
      }
    >
      <div style={_pyGxBox({ marginBottom: 14, padding: 12, background: HR.surfaceAlt, borderRadius: 4, fontSize: FontSizes.trading_md, color: HR.inkSoft })}>
        此期間共 <strong style={{ color: HR.ink, fontFamily: HR.mono }}>{period.payslip_count}</strong> 張薪資單。確認後狀態變為「已發薪」，所有薪資單不可再改。
      </div>
      <Field label="實際發薪日" required>
        <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} style={inputStyle()} />
      </Field>
    </Modal>
  );
}

/* ─────────────────────────  Payslip detail / edit  ────────── */

/* PayslipEditor v2 — driven by payroll_engine.
 *
 * Reads GET /payslips/:id once on open to fetch full breakdown (server-side
 * engine call). HR edits total_salary / bonus / deductions / remark → PATCH
 * triggers server re-calculate → fresh breakdown returned.
 *
 * Layout:
 *   Header (edit fields)         | bank/cash split + key numbers
 *   應發明細 + 應扣明細           |
 *   加班明細 (5 類) + 請假明細    |
 *   雇主負擔 + 列印按鈕           |
 */
function PayslipEditor({ slip: initialSlip, periodStatus, canEdit, onClose, onSaved }) {
  const toast = useToast();
  const canEditFinancials = canEdit && (periodStatus === 'open' || periodStatus === 'calculating');

  const [slip, setSlip] = React.useState(initialSlip);
  const [loading, setLoading] = React.useState(true);
  const [bonus, setBonus] = React.useState(initialSlip.bonus ?? 0);
  const [otherDeduct, setOtherDeduct] = React.useState(() => {
    const arr = tryParsePayrollJSON(initialSlip.deductions_json) || [];
    return arr.reduce((sum, x) => sum + (parseFloat(x.amount) || 0), 0);
  });
  const [remark, setRemark] = React.useState(initialSlip.remark || '');
  const [status, setStatus] = React.useState(initialSlip.status || 'draft');
  const [saving, setSaving] = React.useState(false);

  // total_salary 為 snapshot：從 employee_salary_history 自動帶入；想改月薪請走「薪資歷史」tab。
  const totalSalary = slip.total_salary ?? 0;

  // Fetch full payslip with breakdown on mount
  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    api('GET', `/payroll/payslips/${initialSlip.id}`)
      .then(s => {
        if (!alive) return;
        setSlip(s);
        setBonus(s.bonus ?? 0);
        setRemark(s.remark || '');
        setStatus(s.status || 'draft');
        const arr = tryParsePayrollJSON(s.deductions_json) || [];
        setOtherDeduct(arr.reduce((sum, x) => sum + (parseFloat(x.amount) || 0), 0));
      })
      .catch(e => toast('載入失敗：' + (e.detail || ''), 'error'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [initialSlip.id]);

  const br = slip.breakdown || {};
  // 固定加減項拆分（calculate 時附掛在 breakdown.recurring_detail；舊快照沒有 → 一律防呆不顯示）
  const recAllowances = Array.isArray(br.recurring_detail?.allowances) ? br.recurring_detail.allowances : [];
  const recDeductions = Array.isArray(br.recurring_detail?.deductions) ? br.recurring_detail.deductions : [];
  const recAllowanceSum = recAllowances.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  // 資遣（separation_type='layoff'）月才有：severance_pay 不在 gross/net 內；舊快照無 key → 0
  const severancePay = parseFloat(br.severance_pay) || 0;
  const sevDetail = (br.severance_detail && typeof br.severance_detail === 'object') ? br.severance_detail : {};
  const yearEndMonths = br.year_end_detail && br.year_end_detail.months_in_year != null ? br.year_end_detail.months_in_year : null;
  // 簽收流程狀態(confirming/confirmed)須走簽收作業變更；後端只收 draft/published/viewed，
  // 若把這些狀態值回送 PATCH 會 400/409。故此類薪資單存檔時不帶 status 欄。
  const signFlow = status === 'confirming' || status === 'confirmed';

  const submit = async () => {
    setSaving(true);
    try {
      const isLockedOrPaid = periodStatus === 'locked' || periodStatus === 'paid';
      const deductions_json = otherDeduct > 0
        ? JSON.stringify([{ name: '其他扣款', amount: parseFloat(otherDeduct) || 0 }])
        : null;
      const payload = isLockedOrPaid
        ? {
            remark: remark.trim() || null,
            ...(signFlow ? {} : { status }),
          }
        : {
            bonus: parseFloat(bonus) || 0,
            deductions_json,
            remark: remark.trim() || null,
            ...(signFlow ? {} : { status }),
          };
      const updated = await api('PATCH', `/payroll/payslips/${slip.id}`, payload);
      setSlip(updated);
      toast(isLockedOrPaid ? '已儲存變更' : '已儲存並重算', 'ok');
      onSaved();
    } catch (e) { toast('儲存失敗：' + (e.detail || ''), 'error'); }
    finally { setSaving(false); }
  };

  const print = async () => {
    // 後端產 PDF（reportlab）→ 新分頁開啟，使用者可下載或從瀏覽器列印
    try {
      const token = getToken();
      const url = `${API_BASE}/payroll/payslips/${slip.id}/pdf`;
      // 用 fetch 取 PDF（帶 Authorization header），再 blob URL 開新分頁
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      // 5 分鐘後釋放 blob URL（防 memory leak；新分頁此時應已載完 PDF）
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
    } catch (e) {
      toast('產生 PDF 失敗：' + (e.message || e), 'error');
    }
  };

  // 員工線上確認已改為紙本簽收流程（列印 → 簽名 → HR 登記），
  // 原 confirmPayslip（POST /payslips/{id}/confirm）後端端點已停用（回 410）；紙本簽收（mark-signed）為唯一路徑。

  if (loading) {
    return (
      <Modal open onClose={onClose} title="薪資單" size="lg">
        <div style={{ padding: 60, display: 'grid', placeItems: 'center' }}><Spinner size={20} /></div>
      </Modal>
    );
  }

  return (
    <Modal
      open onClose={onClose}
      title={`薪資單 — ${slip.employee_name || '#' + slip.employee_id}`}
      subtitle={`${slip.period_year}年${slip.period_month}月 · ${PAYSLIP_STATUS[slip.status]?.label || slip.status}`}
      size="lg"
      className="payslip-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>關閉</Button>
          <Button variant="ghost" icon="printer" onClick={print}>列印</Button>
          {canEdit && (
            <Button variant="primary" onClick={submit} disabled={saving}>
              {saving ? '儲存中…' : (canEditFinancials ? '儲存並重算' : '儲存變更')}
            </Button>
          )}
        </>
      }
    >
      {/* 列印用 style — 隱藏 sidebar/header/backdrop，把 modal 內容鋪滿頁 */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .payslip-modal, .payslip-modal * { visibility: visible !important; }
          .payslip-modal { position: fixed !important; inset: 0 !important;
            max-width: 100% !important; max-height: 100% !important;
            margin: 0 !important; box-shadow: none !important;
            background: white !important; }
          .payslip-modal .modal-footer, .payslip-modal .modal-close { display: none !important; }
        }
      `}</style>

      {!canEditFinancials && (
        <div style={_pyGxBox({ marginBottom: 14, padding: 10, background: HR.surfaceAlt, color: HR.inkSoft, borderRadius: 4, fontSize: FontSizes.xxs })}>
          {canEdit
            ? `期間狀態為「${PERIOD_STATUS[periodStatus]?.label || periodStatus}」— 金額欄位唯讀，僅能修改處理狀態與備註。`
            : '您僅能檢視此薪資單。'}
        </div>
      )}

      {/* === 編輯區 (HR-only) === */}
      {canEdit && (
        <div style={uiStyle({
          marginBottom: 18, padding: 14, background: HR.surfaceAlt, borderRadius: 5,
          border: `1px solid ${HR.rule}`,
        }, { padding: 16, background: GxT.color.s1, borderRadius: GxT.radius.card, border: `1px solid ${GxT.color.olv}` })}>
          <div style={_pyGxLabel({
            fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.1em',
            textTransform: 'uppercase', marginBottom: 10,
          })}>HR 輸入欄位</div>
          <div className={GxCore.cls('hr-form-grid', 'gx-py-form')} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <Field
              label="月薪"
              hint="來自員工薪資歷史；想改月薪請去「薪資歷史」tab 調薪"
              span={2}
            >
              <div style={{
                ...inputStyle({ fontFamily: HR.mono, textAlign: 'right', fontSize: FontSizes.base }),
                background: HR.surface, color: HR.inkSoft, cursor: 'not-allowed',
              }}>
                {fmtMoney(totalSalary)}
              </div>
            </Field>
            <Field label="變動獎金" hint={recAllowances.length ? '另含固定加項，此欄僅填當月增減' : undefined} span={1}>
              <input type="number" step="any" value={bonus} onChange={(e) => setBonus(e.target.value)}
                disabled={!canEditFinancials}
                style={inputStyle({
                  fontFamily: HR.mono,
                  textAlign: 'right',
                  ...(!canEditFinancials ? { background: HR.surface, color: HR.inkSoft, cursor: 'not-allowed' } : {})
                })} />
            </Field>
            <Field label="其他扣款" hint={recDeductions.length ? '另含固定減項，此欄僅填當月手動' : 'HR 手動加扣'} span={1}>
              <input type="number" step="any" value={otherDeduct} onChange={(e) => setOtherDeduct(e.target.value)}
                disabled={!canEditFinancials}
                style={inputStyle({
                  fontFamily: HR.mono,
                  textAlign: 'right',
                  ...(!canEditFinancials ? { background: HR.surface, color: HR.inkSoft, cursor: 'not-allowed' } : {})
                })} />
            </Field>
            <Field label="處理狀態" span={1}>
              {signFlow ? (
                // 簽收流程狀態不可用此下拉表示/變更（會被誤降級為草稿）；顯示唯讀標籤。
                <input
                  type="text"
                  value={PAYSLIP_STATUS[status]?.label || status}
                  readOnly
                  title="簽收流程狀態須透過簽收作業變更"
                  style={inputStyle({ width: '100%', background: HR.surface, color: HR.inkSoft, cursor: 'not-allowed' })}
                />
              ) : (
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={inputStyle({ width: '100%' })}
                >
                  <option value="draft">草稿 (Draft)</option>
                  <option value="published">已公布 (Published)</option>
                  <option value="viewed">已查看 (Viewed)</option>
                </select>
              )}
            </Field>
            <Field label="備註" span={3}>
              <Textarea value={remark} onChange={setRemark} rows={2} />
            </Field>
          </div>
        </div>
      )}

      {/* === 摘要區（4 卡） === */}
      <div className="hr-payroll-summary-grid" style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
        marginBottom: 18,
      }}>
        <SummaryCard label="應發合計" value={br.gross_pay} accent="ink" />
        <SummaryCard label="應扣合計" value={br.total_deduction} accent="down" />
        <SummaryCard label="實領" value={br.net_pay} accent="accent" emphasized />
        <SummaryCard
          label="應出勤 / 實際"
          value={`${((br.expected_work_minutes || 0) / 60).toFixed(1)} / ${((br.actual_work_minutes || 0) / 60).toFixed(1)} H`}
          accent={br.absence_minutes > 0 ? 'warn' : 'ink'}
        />
      </div>

      {/* === 銀行/現金拆分 === */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
        marginBottom: 18,
      }}>
        <SummaryCard label="存入銀行" value={br.bank_amount} accent="accent" />
        <SummaryCard label="領取現金" value={br.cash_amount} accent="warn" />
      </div>

      {/* === 應發明細 + 應扣明細 === */}
      <div className="hr-payslip-panels" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>
        <BreakdownPanel
          title="應發明細"
          accent={HR.accent}
          rows={[
            { label: br.is_partial_month ? '原薪水(未滿整月依天數)' : '原薪水', value: br.base_pay_anchor, framed: true },
            { label: `缺勤扣款 (${((br.absence_minutes || 0) / 60).toFixed(1)} H)`, value: -(br.absence_deduct || 0), faint: !br.absence_deduct },
            { label: '病假回補 (半薪)', value: br.leave_sick_refund, faint: !br.leave_sick_refund },
            { label: '全薪假回補 (特休等)', value: br.leave_paid_refund, faint: !br.leave_paid_refund },
            { label: '加班費', value: br.overtime_pay, faint: !br.overtime_pay },
            {
              label: '獎金', value: br.bonus, faint: !br.bonus && !recAllowances.length,
              sub: recAllowances.length
                ? `含固定加項 ${recAllowances.length} 筆共 ${fmtMoney(recAllowanceSum)}（手動欄僅填當月增減）`
                : null,
            },
            { label: `午餐津貼 (${slip.period_month % 12 + 1}月份)`, value: br.lunch_allowance, faint: !br.lunch_allowance },
            // 年終保障獎金（資遣月才有；合約保障一個月本薪 × 當年度在職月數/12，走獎金路徑進應發／應稅）
            {
              label: `年終保障獎金${yearEndMonths ? ` (在職 ${yearEndMonths}/12)` : ''}`,
              value: br.year_end_guarantee || 0, faint: !br.year_end_guarantee,
              sub: br.year_end_guarantee && br.year_end_detail
                ? `一個月本薪 ${fmtMoney(br.year_end_detail.monthly_base || 0)} × ${yearEndMonths}/12`
                : null,
            },
          ]}
          total={br.gross_pay}
          totalLabel="應發合計"
        />
        <BreakdownPanel
          title="應扣明細"
          accent={HR.down}
          rows={[
            { label: '勞保 (員工)', value: br.labor_ins_emp },
            { label: '健保 (員工)', value: br.health_ins_emp },
            { label: '二代健保', value: br.second_health, faint: !br.second_health },
            {
              label: '其他扣款', value: br.other_deduct, faint: !br.other_deduct && !recDeductions.length,
              sub: recDeductions.length ? (
                <>
                  {recDeductions.map((d, i) => (
                    <div key={i}>固定減項：{d.name} {fmtMoney(parseFloat(d.amount) || 0)}</div>
                  ))}
                </>
              ) : null,
            },
          ]}
          total={br.total_deduction}
          totalLabel="應扣合計"
        />
      </div>

      {/* === 資遣費（非薪資所得；只在資遣月顯示）===
       * 資遣費不進 gross_pay / net_pay（不計二代健保、不列薪資所得），與實領分開呈現，
       * 最後以「本期實付合計」= 實領 + 資遣費 作結；bank_amount 已含兩項（全額進銀行）。 */}
      {severancePay > 0 && (
        <div style={{ marginBottom: 18 }}>
          <BreakdownPanel
            title="資遣費（非薪資所得）"
            accent={HR.warn}
            rows={[
              { label: '實領（薪資）', value: br.net_pay },
              {
                label: `資遣費${sevDetail.service_months != null ? ` (年資 ${sevDetail.service_months} 個月)` : ''}`,
                value: severancePay,
                sub: sevDetail.average_wage != null
                  ? `平均工資 ${fmtMoney(sevDetail.average_wage)} × 0.5 × ${sevDetail.service_months}/12`
                    + (sevDetail.capped ? `（已達上限 ${sevDetail.cap_months || 6} 個月平均工資）` : '')
                  : '非薪資所得，不計二代健保，另列退職所得申報',
              },
            ]}
            total={br.total_payout != null ? br.total_payout : (br.net_pay || 0) + severancePay}
            totalLabel="本期實付合計"
          />
        </div>
      )}

      {/* === 加班 5 類明細（有加班才顯示） === */}
      {br.overtime_detail && br.overtime_detail.total_hours > 0 && (
        <div style={uiStyle({
          marginBottom: 18, padding: 14, background: HR.surface,
          border: `1px solid ${HR.rule}`, borderRadius: 5,
        }, { padding: 16, borderRadius: GxT.radius.inner })}>
          <div style={_pyGxLabel({
            fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.1em',
            textTransform: 'uppercase', marginBottom: 10,
          })}>加班明細 · 5 類別</div>
          <table style={{ width: '100%', fontSize: FontSizes.xxs, fontFamily: HR.mono }}>
            <thead>
              <tr style={{ color: HR.inkFaint, fontSize: FontSizes.trading_md, textTransform: 'uppercase' }}>
                <th style={{ textAlign: 'left', fontWeight: 500, padding: '4px 8px' }}>類別</th>
                <th style={{ textAlign: 'right', fontWeight: 500, padding: '4px 8px' }}>小時數</th>
                <th style={{ textAlign: 'right', fontWeight: 500, padding: '4px 8px' }}>倍率</th>
                <th style={{ textAlign: 'right', fontWeight: 500, padding: '4px 8px' }}>金額</th>
              </tr>
            </thead>
            <tbody>
              <OTRow label="平日加班 前 2H" hours={br.overtime_detail.wd_h1_hours} mult="× 1.34" pay={br.overtime_detail.wd_h1_pay} />
              <OTRow label="平日加班 第 3-4H" hours={br.overtime_detail.wd_h2_hours} mult="× 1.67" pay={br.overtime_detail.wd_h2_pay} />
              <OTRow label="休息日 前 2H" hours={br.overtime_detail.rd_h1_hours} mult="× 1.34" pay={br.overtime_detail.rd_h1_pay} />
              <OTRow label="休息日 第 3H+" hours={br.overtime_detail.rd_h2_hours} mult="× 1.67" pay={br.overtime_detail.rd_h2_pay} />
              <OTRow label="例假/國定假日" hours={br.overtime_detail.holiday_hours} mult="× 2.67" pay={br.overtime_detail.holiday_pay} />
              <tr style={{ borderTop: `1px solid ${HR.rule}` }}>
                <td colSpan={2} style={{ padding: '6px 8px', color: HR.inkFaint }}>
                  時薪 NT$ {br.overtime_detail.hourly_rate?.toFixed(2)}
                </td>
                <td style={{ textAlign: 'right', padding: '6px 8px', color: HR.inkFaint }}>
                  總計 {br.overtime_detail.total_hours.toFixed(1)} H
                </td>
                <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>
                  {fmtMoney(br.overtime_pay)}
                </td>
              </tr>
              {br.overtime_detail.taxable_pay > 0 && (
                <tr>
                  <td colSpan={4} style={uiStyle({ padding: '4px 8px', fontSize: FontSizes.trading_lg, color: HR.warn, fontStyle: 'italic' }, { fontStyle: 'normal', fontWeight: 700, color: '#8A5A1A' })}>
                    ⚠ 加班 &gt; 46H/月 — 免稅 {fmtMoney(br.overtime_detail.exempt_pay)} · 應稅 {fmtMoney(br.overtime_detail.taxable_pay)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* === 雇主負擔 === */}
      <div className="hr-payroll-employer-grid" style={uiStyle({
        padding: 14, background: HR.surfaceAlt, borderRadius: 5,
        border: `1px solid ${HR.rule}`,
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, fontSize: FontSizes.xxs,
      }, { padding: 16, background: GxT.color.s2, borderRadius: GxT.radius.inner, border: 'none' })}>
        <div style={_pyGxLabel({
          fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.1em',
          textTransform: 'uppercase', gridColumn: 'span 4', marginBottom: 4,
        })}>雇主負擔 · 人力成本</div>
        <div>
          <div style={{ color: HR.inkFaint, fontSize: FontSizes.trading_lg }}>勞保 (雇主)</div>
          <div style={{ fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(br.labor_ins_employer)}</div>
        </div>
        <div>
          <div style={{ color: HR.inkFaint, fontSize: FontSizes.trading_lg }}>健保 (雇主)</div>
          <div style={{ fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(br.health_ins_employer)}</div>
        </div>
        <div>
          <div style={{ color: HR.inkFaint, fontSize: FontSizes.trading_lg }}>勞退提繳 (6%)</div>
          <div style={{ fontFamily: HR.mono, fontWeight: 600 }}>{fmtMoney(br.labor_pension)}</div>
        </div>
        <div>
          <div style={{ color: HR.inkFaint, fontSize: FontSizes.trading_lg }}>人力成本合計</div>
          <div style={{ fontFamily: HR.mono, fontWeight: 700, color: HR.ink }}>{fmtMoney(br.employer_cost_total)}</div>
        </div>
      </div>
    </Modal>
  );
}

/* ───────  Helper components for breakdown display  ─────── */

function SummaryCard({ label, value, accent, emphasized }) {
  const color = accent === 'accent' ? HR.accent
    : accent === 'down' ? HR.down
      : accent === 'warn' ? HR.warn
        : HR.ink;
  return (
    <div style={uiStyle({
      padding: '12px 14px', background: HR.surface,
      border: `1px solid ${HR.rule}`, borderRadius: 5,
    }, {
      // tonal KPI tile (mockup .kpis): primary-container for the emphasised net-pay tile
      padding: '12px 14px', border: 'none', borderRadius: GxT.radius.inner,
      background: emphasized ? GxT.color.pc : GxT.color.s2,
    })}>
      <div style={_pyGxLabel({
        fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.1em',
        textTransform: 'uppercase',
      })}>{label}</div>
      <div style={uiStyle({
        fontFamily: HR.mono, fontSize: emphasized ? 22 : 17,
        fontWeight: emphasized ? 700 : 600,
        color, marginTop: 4,
      }, {
        // same text, GX display numerals; amber warn text → AA-safe darker amber on s2
        fontFamily: GxT.font, fontWeight: emphasized ? 900 : 800, fontVariantNumeric: 'tabular-nums',
        color: accent === 'warn' ? '#8A5A1A' : (accent === 'accent' && emphasized ? GxT.color.onPc : color),
      })}>
        {typeof value === 'number' ? fmtMoney(value) : (value ?? '—')}
      </div>
    </div>
  );
}

function BreakdownPanel({ title, accent, rows, total, totalLabel }) {
  return (
    <div style={uiStyle({
      padding: 14, background: HR.surface,
      border: `1px solid ${HR.rule}`, borderRadius: 5,
    }, { padding: 16, borderRadius: GxT.radius.inner })}>
      <div style={_pyGxLabel({
        fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.1em',
        textTransform: 'uppercase', marginBottom: 10, paddingBottom: 6,
        borderBottom: `2px solid ${accent}`,
      })}>{title}</div>
      <table style={{ width: '100%', fontSize: FontSizes.xxs }}>
        <tbody>
          {rows.map((r, i) => {
            if (r.framed) {
              return (
                <tr key={i}>
                  <td style={{
                    padding: '9px 12px 9px 10px',
                    background: HR.surfaceAlt,
                    borderLeft: `3px solid ${HR.accent}`,
                    color: HR.ink,
                    fontWeight: 500,
                  }}>
                    {r.label}
                  </td>
                  <td style={{
                    padding: '9px 12px',
                    background: HR.surfaceAlt,
                    textAlign: 'right',
                    fontFamily: HR.mono,
                    fontWeight: 700,
                    color: HR.accent,
                    fontSize: FontSizes.sm,
                    letterSpacing: '0.02em',
                  }}>
                    {fmtMoney(r.value)}
                  </td>
                </tr>
              );
            }
            return (
              <tr key={i} style={{ opacity: r.faint ? 0.4 : 1 }}>
                <td style={{ padding: '4px 0', color: HR.inkSoft }}>
                  {r.label}
                  {r.sub && (
                    <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 2 }}>
                      {r.sub}
                    </div>
                  )}
                </td>
                <td style={{ padding: '4px 0', textAlign: 'right', fontFamily: HR.mono, verticalAlign: 'top' }}>
                  {fmtMoney(r.value)}
                </td>
              </tr>
            );
          })}
          <tr style={{ borderTop: `1px solid ${HR.rule}` }}>
            <td style={_pyGxLabel({ padding: '8px 0 0', fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.05em', textTransform: 'uppercase' })}>
              {totalLabel}
            </td>
            <td style={{ padding: '8px 0 0', textAlign: 'right', fontFamily: HR.mono, fontWeight: uiPick(700, 800), fontSize: FontSizes.sm, color: accent }}>
              {fmtMoney(total)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function OTRow({ label, hours, mult, pay }) {
  const isZero = !hours || hours === 0;
  return (
    <tr style={{ opacity: isZero ? 0.35 : 1 }}>
      <td style={{ padding: '4px 8px', color: HR.inkSoft }}>{label}</td>
      <td style={{ padding: '4px 8px', textAlign: 'right' }}>{(hours || 0).toFixed(1)} H</td>
      <td style={{ padding: '4px 8px', textAlign: 'right', color: HR.inkFaint }}>{mult}</td>
      <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: isZero ? 400 : 600 }}>
        {fmtMoney(pay || 0)}
      </td>
    </tr>
  );
}

/* ─────────────────────────  Period detail (HR view)  ──────── */

function PeriodDetail({ period, isHR, isFinance, onBack, onChanged }) {
  const toast = useToast();
  const [payslips, setPayslips] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [showPay, setShowPay] = React.useState(false);
  const [showLockConfirm, setShowLockConfirm] = React.useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [printing, setPrinting] = React.useState(false);
  const [markingSigned, setMarkingSigned] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const resp = await api('GET', `/payroll/payslips?period_id=${period.id}&page_size=200`).catch(() => ({ items: [] }));
    setPayslips(resp.items || []);
    setSelectedIds(new Set());
    setLoading(false);
  }, [period.id]);

  React.useEffect(() => { refresh(); }, [refresh]);
  React.useEffect(() => { setSelectedIds(new Set()); }, [period.id]);

  const status = period.status;
  // 紙本簽收流程：確認薪資(verifying)後即可列印發放，簽名回收後 HR 批次登記簽收
  const canBatchPrint = status === 'verifying' || status === 'locked' || status === 'paid';
  const canMarkSigned = isHR && canBatchPrint;

  const markSelectedSigned = async () => {
    if (selectedIds.size === 0) return;
    setMarkingSigned(true);
    try {
      const resp = await api('POST', '/payroll/payslips/mark-signed', {
        payslip_ids: Array.from(selectedIds),
      });
      toast(`已登記簽收 ${resp.updated} 筆` + (resp.skipped ? `，略過 ${resp.skipped} 筆（已簽收或狀態不符）` : ''), 'ok');
      await refresh();
    } catch (e) {
      toast('登記簽收失敗：' + (e.detail || e.message || ''), 'error');
    } finally {
      setMarkingSigned(false);
    }
  };

  const toggleOne = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(payslips.map(s => s.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const printSelectedPayslips = async () => {
    if (selectedIds.size === 0) return;
    setPrinting(true);
    try {
      const token = getToken();
      const url = `${API_BASE}/payroll/periods/${period.id}/payslips/pdf`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payslip_ids: Array.from(selectedIds) }),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j.detail) msg = j.detail; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
    } catch (e) {
      toast('產生批次 PDF 失敗：' + (e.message || e), 'error');
    } finally {
      setPrinting(false);
    }
  };

  const action = async (verb, label) => {
    setBusy(true);
    try {
      await api('POST', `/payroll/periods/${period.id}/${verb}`);
      toast(`已執行：${label}`, 'ok');
      await refresh();
      onChanged();
    } catch (e) { toast(`${label}失敗：` + (e.detail || ''), 'error'); }
    finally { setBusy(false); }
  };

  const totals = payslips.reduce((acc, s) => {
    acc.gross += s.gross_pay || 0;
    acc.net += s.net_pay || 0;
    return acc;
  }, { gross: 0, net: 0 });

  const confirmedCount = payslips.filter(s => s.status === 'confirmed').length;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <Button size="sm" variant="ghost" onClick={onBack}>← 回期間列表</Button>
        <h2 style={{ fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: uiPick(500, 800), margin: 0 }}>
          {period.year} 年 {period.month} 月薪資
        </h2>
        <Pill tone={PERIOD_STATUS[status]?.tone || 'neutral'} dot>{PERIOD_STATUS[status]?.label || status}</Pill>
        {period.payslip_count > 0 && (
          <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, fontFamily: HR.mono }}>
            {payslips.length} 張 · 應發 {fmtMoney(totals.gross)} · 實領 {fmtMoney(totals.net)}
          </span>
        )}
      </div>

      {isHR && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
          {status === 'open' && period.payslip_count === 0 && (
            <Button variant="primary" onClick={() => action('generate', '產生薪資單')} disabled={busy}>① 產生薪資單（依在職員工）</Button>
          )}
          {status === 'open' && period.payslip_count > 0 && (
            <Button variant="primary" onClick={() => action('calculate', '計算薪資')} disabled={busy}>② 計算 (overtime + gross/net)</Button>
          )}
          {status === 'calculating' && (
            <>
              <Button variant="ghost" onClick={() => action('generate', '補建薪資單')} disabled={busy}>← 補建薪資單（新增員工）</Button>
              <Button variant="secondary" onClick={() => action('calculate', '重新計算')} disabled={busy}>重新計算</Button>
              <Button variant="primary" onClick={() => action('send-confirmation', '確認薪資（進入列印簽收）')} disabled={busy}>③ 確認薪資（進入列印簽收）</Button>
            </>
          )}
          {status === 'verifying' && (
            <>
              <Button variant="secondary" onClick={() => setShowUnlockConfirm(true)} disabled={busy}>返回計算中（可修改）</Button>
              <Button variant="primary" onClick={() => setShowLockConfirm(true)} disabled={busy}>④ 鎖定</Button>
              <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, fontFamily: HR.mono, alignSelf: 'center', marginLeft: 8 }}>
                簽收進度：{confirmedCount} / {payslips.length} 筆已簽收
              </span>
            </>
          )}
          {status === 'locked' && (
            <>
              <Button variant="primary" onClick={() => action('publish', '公布薪資單')} disabled={busy}>⑤ 公布薪資單（員工可見）</Button>
              {isFinance && <Button variant="primary" onClick={() => setShowPay(true)} disabled={busy}>⑥ 標記已發薪</Button>}
              <Button variant="secondary" onClick={() => setShowUnlockConfirm(true)} disabled={busy}>解鎖期間 (返回計算中)</Button>
            </>
          )}
          {status === 'paid' && (
            <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, fontFamily: HR.mono, alignSelf: 'center' }}>
              已於 {period.pay_date || '—'} 發薪 · 流程結束
            </span>
          )}
        </div>
      )}

      {loading ? (
        <SkeletonTable rows={6} />
      ) : payslips.length === 0 ? (
        <EmptyState icon="dollar-sign" title="此期間尚無薪資單" hint={isHR && status === 'open' ? '請先點「產生薪資單」依在職員工建立 draft。' : '—'} />
      ) : (
        <Card padding="0" eyebrow="Payslips" title="薪資單清單" italic={`${payslips.length} 張`} right={
          canBatchPrint && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="primary"
                size="md"
                icon="printer"
                onClick={printSelectedPayslips}
                disabled={selectedIds.size === 0 || printing}
              >
                {printing ? '列印中…' : `列印選取 (${selectedIds.size})`}
              </Button>
              {canMarkSigned && (
                <Button
                  variant="secondary"
                  size="md"
                  icon="check"
                  onClick={markSelectedSigned}
                  disabled={selectedIds.size === 0 || markingSigned}
                >
                  {markingSigned ? '登記中…' : `標記已簽收 (${selectedIds.size})`}
                </Button>
              )}
            </div>
          )
        }>
          <div className="va2-table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <thead>
                <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                  {canBatchPrint && (
                    <th style={{ padding: '11px 8px 11px 24px', textAlign: 'left', fontWeight: 500, width: 32 }}>
                      <input
                        type="checkbox"
                        aria-label="全選 / 全不選"
                        title="全選 / 全不選"
                        checked={payslips.length > 0 && selectedIds.size === payslips.length}
                        ref={el => {
                          if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < payslips.length;
                        }}
                        onClick={e => e.stopPropagation()}
                        onChange={e => toggleAll(e.target.checked)}
                      />
                    </th>
                  )}
                  <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>員工</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>原薪水</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>加班</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>獎金</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>應發</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>實領</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>狀態</th>
                  <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {payslips.map(s => {
                  let br = null;
                  try { br = s.attendance_summary_json ? JSON.parse(s.attendance_summary_json) : null; } catch { br = null; }
                  const baseAnchor = br?.base_pay_anchor ?? s.base_salary;
                  const isPartial = !!br?.is_partial_month;
                  return (
                    <tr key={s.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                      {canBatchPrint && (
                        <td style={{ padding: '13px 8px 13px 24px', width: 32 }}>
                          <input
                            type="checkbox"
                            aria-label={`選取 ${s.employee_name || '#' + s.id}`}
                            checked={selectedIds.has(s.id)}
                            onClick={e => e.stopPropagation()}
                            onChange={e => toggleOne(s.id, e.target.checked)}
                          />
                        </td>
                      )}
                      <td style={{ padding: '13px 16px 13px 24px', fontWeight: 500 }}>{s.employee_name || <span style={{ color: HR.inkFaint, fontWeight: 400 }}>—</span>}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }} title={isPartial ? '原薪水(未滿整月依天數)' : '原薪水'}>{fmtMoney(baseAnchor)}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: HR.inkSoft }}>{fmtMoney(s.overtime_pay)}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: HR.inkSoft }}>{fmtMoney(s.bonus)}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{fmtMoney(s.gross_pay)}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600, color: HR.accent }}>
                        {fmtMoney(s.net_pay)}
                        {(parseFloat(s.severance_pay) || 0) > 0 && (
                          <div style={{ fontSize: FontSizes.trading_lg, color: HR.warn, fontWeight: 500 }}>＋資遣費 {fmtMoney(s.severance_pay)}</div>
                        )}
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <Pill tone={PAYSLIP_STATUS[s.status]?.tone || 'neutral'} dot>{PAYSLIP_STATUS[s.status]?.label || s.status}</Pill>
                      </td>
                      <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                        <Button size="sm" variant="ghost" onClick={() => setSelected(s)}>{isHR ? '編輯' : '檢視'}</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {selected && (
        <PayslipEditor
          slip={selected} periodStatus={status} canEdit={isHR}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); refresh(); }}
        />
      )}
      {showPay && (
        <PayPeriodModal
          period={period}
          onCancel={() => setShowPay(false)}
          onSaved={() => { setShowPay(false); refresh(); onChanged(); }}
        />
      )}
      <ConfirmDialog
        open={showLockConfirm}
        title="鎖定薪資期間"
        message={`確定要鎖定 ${period.year} 年 ${period.month} 月的薪資期間嗎？鎖定後將無法再進行計算或修改任何薪資單。`}
        danger
        confirmLabel="確認鎖定"
        busy={busy}
        onCancel={() => setShowLockConfirm(false)}
        onConfirm={async () => {
          setShowLockConfirm(false);
          await action('lock', '鎖定');
        }}
      />
      <ConfirmDialog
        open={showUnlockConfirm}
        title="解鎖薪資期間"
        message={`確定要解鎖 ${period.year} 年 ${period.month} 月的薪資期間嗎？解鎖後期間將變回「計算中」，所有薪資單將重設為「草稿」，已登記的紙本簽收紀錄將全部清除（金額重算後原簽名單即失效），需重新列印、重新收單。`}
        confirmLabel="確認解鎖"
        busy={busy}
        onCancel={() => setShowUnlockConfirm(false)}
        onConfirm={async () => {
          setShowUnlockConfirm(false);
          await action('unlock', '解鎖期間');
        }}
      />
    </>
  );
}

/* ─────────────────────────  Period list (HR/finance) ───────── */

function PeriodsList({ periods, onSelect, onNew, isHR, onChanged }) {
  const toast = useToast();
  const [confirmDelete, setConfirmDelete] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api('DELETE', `/payroll/periods/${confirmDelete.id}`);
      toast('已刪除', 'ok');
      setConfirmDelete(null);
      onChanged();
    } catch (e) { toast('刪除失敗：' + (e.detail || ''), 'error'); }
    finally { setDeleting(false); }
  };

  return (
    <>
      <Card padding="0" eyebrow="Payroll Periods" title="薪資期間" italic={`${periods.length} 個期間`} right={
        isHR && <Button variant="primary" size="md" icon="plus" onClick={onNew}>新增期間</Button>
      }>
        {periods.length === 0 ? (
          <EmptyState icon="dollar-sign" title="尚未建立任何薪資期間" hint={isHR ? '點右上角新增第一個。' : '—'} />
        ) : (
          <div className="va2-table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <thead>
                <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                  <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>期間</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>發薪日</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>薪資單</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>狀態</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>備註</th>
                  <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {periods.map(p => (
                  <tr key={p.id} style={{ borderTop: `1px solid ${HR.ruleSoft}`, cursor: 'pointer' }} onClick={() => onSelect(p)}>
                    <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.serif, fontSize: FontSizes.base, fontWeight: uiPick(500, 700) }}>
                      {p.year} / {String(p.month).padStart(2, '0')}
                    </td>
                    <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>{p.pay_date || '—'}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{p.payslip_count}</td>
                    <td style={{ padding: '13px 16px' }}>
                      <Pill tone={PERIOD_STATUS[p.status]?.tone || 'neutral'} dot>{PERIOD_STATUS[p.status]?.label || p.status}</Pill>
                    </td>
                    <td style={_pyGxUpright({ padding: '13px 16px', color: HR.inkSoft, fontSize: FontSizes.xxs, fontStyle: p.note ? 'italic' : 'normal', fontFamily: HR.serif })}>
                      {p.note || '—'}
                    </td>
                    <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      {isHR && p.status === 'open' && p.payslip_count === 0 && (
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(p)} style={{ color: HR.down }}>刪除</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => onSelect(p)}>檢視</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <ConfirmDialog
        open={!!confirmDelete}
        title="刪除薪資期間"
        message={confirmDelete ? `確定刪除 ${confirmDelete.year}/${confirmDelete.month} 期間？此操作無法復原。` : ''}
        danger busy={deleting}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doDelete}
      />
    </>
  );
}

/* ─────────────────────────  Me view  ───────────────────────── */

function MyPayslipsTab() {
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [slips, setSlips] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const list = await api('GET', `/payroll/payslips/me?year=${year}`).catch(() => []);
    setSlips(Array.isArray(list) ? list : []);
    setLoading(false);
  }, [year]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const openSlip = async (s) => {
    setSelected(s);
    if (s.status === 'published') {
      try {
        const updated = await api('POST', `/payroll/payslips/${s.id}/view`);
        setSelected(updated);
        refresh();
      } catch { /* silent */ }
    }
  };

  return (
    <>
      <Card padding="0" eyebrow="My Payslips" title="我的薪資單" italic={`${year} 年 · ${slips.length} 張`} right={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint }}>年度</span>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} style={inputStyle({ width: 110, fontFamily: HR.mono })}>
            {[year - 2, year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      }>
        {loading ? (
          <SkeletonTable rows={6} />
        ) : slips.length === 0 ? (
          <EmptyState icon="dollar-sign" title={`${year} 年尚無薪資單`} hint="HR 確認薪資後此處會出現。" />
        ) : (
          <div className="va2-table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <thead>
                <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                  <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>期間</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>原薪水</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>加班</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>獎金</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>應發</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>實領</th>
                  <th style={{ padding: '11px 24px 11px 16px', textAlign: 'left', fontWeight: 500 }}>狀態</th>
                </tr>
              </thead>
              <tbody>
                {slips.map(s => (
                  <tr key={s.id} style={{ borderTop: `1px solid ${HR.ruleSoft}`, cursor: 'pointer' }} onClick={() => openSlip(s)}>
                    <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.serif, fontWeight: uiPick(500, 700) }}>
                      {s.period_year} / {String(s.period_month).padStart(2, '0')}
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{fmtMoney(s.base_salary)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: HR.inkSoft }}>{fmtMoney(s.overtime_pay)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, color: HR.inkSoft }}>{fmtMoney(s.bonus)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono }}>{fmtMoney(s.gross_pay)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600, color: HR.accent }}>
                        {fmtMoney(s.net_pay)}
                        {(parseFloat(s.severance_pay) || 0) > 0 && (
                          <div style={{ fontSize: FontSizes.trading_lg, color: HR.warn, fontWeight: 500 }}>＋資遣費 {fmtMoney(s.severance_pay)}</div>
                        )}
                      </td>
                    <td style={{ padding: '13px 24px 13px 16px' }}>
                      <Pill tone={PAYSLIP_STATUS[s.status]?.tone || 'neutral'} dot>{PAYSLIP_STATUS[s.status]?.label || s.status}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {selected && (
        <PayslipEditor
          slip={selected} periodStatus={selected.period_status} canEdit={false}
          onClose={() => setSelected(null)}
          onSaved={() => setSelected(null)}
        />
      )}
    </>
  );
}

/* ─────────────────────────  Page entry  ─────────────────────── */

/* ─────────────────────────  Salary History tab  ──────────── */

function toMonthStart(s) {
  // 'YYYY-MM-DD' or 'YYYY-MM-...' → 'YYYY-MM-01'
  if (!s || s.length < 7) return '';
  return s.slice(0, 7) + '-01';
}

function SalaryAdjustModal({ employee, editing, onCancel, onSaved }) {
  const toast = useToast();
  const isEdit = !!editing;
  const [salary, setSalary] = React.useState(editing?.total_salary ?? '');
  const [effectiveFrom, setEffectiveFrom] = React.useState(
    editing?.effective_from || toMonthStart(todayStr()),
  );
  const [note, setNote] = React.useState(editing?.note || '');
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    const sal = parseFloat(salary);
    if (!(sal > 0)) { toast('月薪必填且須大於 0', 'error'); return; }
    const eff = toMonthStart(effectiveFrom);
    if (!eff) { toast('生效日必填', 'error'); return; }

    setSubmitting(true);
    try {
      const payload = { total_salary: sal, effective_from: eff, note: note.trim() || null };
      if (isEdit) {
        await api('PATCH', `/employees/${employee.id}/salaries/${editing.id}`, payload);
        toast('調薪紀錄已更新', 'ok');
      } else {
        await api('POST', `/employees/${employee.id}/salaries`, payload);
        toast(`已新增 ${eff} 生效的調薪 ${fmtMoney(sal)}`, 'ok');
      }
      onSaved();
    } catch (e) {
      toast((isEdit ? '更新' : '建立') + '失敗：' + (e.detail || ''), 'error');
    } finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onCancel}
      title={isEdit ? `修正調薪 — ${employee.name}` : `新增調薪 — ${employee.name}`}
      subtitle="Salary Adjustment"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting ? '處理中…' : (isEdit ? '更新' : '建立')}
          </Button>
        </>
      }
    >
      <div className={GxCore.cls('hr-form-grid', 'gx-py-form')} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="新月薪 (total_salary)" required>
          <input type="number" step="any" value={salary} onChange={(e) => setSalary(e.target.value)}
            style={inputStyle({ fontFamily: HR.mono, textAlign: 'right' })} />
        </Field>
        <Field label="生效日（自動套用該月 1 號）" required>
          <input type="date" value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            style={inputStyle()} />
        </Field>
        <Field label="備註 / 調薪原因" span={2}>
          <Textarea value={note} onChange={setNote} rows={3} />
        </Field>
      </div>
      <div style={_pyGxBox({
        marginTop: 12, padding: 10, fontSize: FontSizes.xxs, color: HR.inkSoft,
        background: HR.surfaceAlt, borderRadius: 4,
      })}>
        實際生效日將強制為 <strong style={{ fontFamily: HR.mono, color: HR.ink }}>
          {toMonthStart(effectiveFrom) || '—'}</strong>（月初規則）。
        計薪時會撈當月 1 號當下生效中的最新一筆。
      </div>
    </Modal>
  );
}


function SalariesTab() {
  const toast = useToast();
  const [employees, setEmployees] = React.useState([]);
  const [selectedEmpId, setSelectedEmpId] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showAdjust, setShowAdjust] = React.useState(false);
  const [editingSalary, setEditingSalary] = React.useState(null);
  const [confirmDelete, setConfirmDelete] = React.useState(null);

  // 撈在職員工列表（一次性 — HR 看到的人數小）
  // /employees（清單）對 HR 關閉（避免開放員工/薪資模組），改打 /attendance/roster：
  // HR 已可存取，回傳「陣列」（每筆 id/name/emp_code/position/department_*），非分頁物件。
  // roster 不含 total_salary（敏感欄位），故下拉選項僅顯示姓名 + 員編；
  // 各員工的薪資歷史在選取後另由 /employees/{id}/salaries 載入。
  React.useEffect(() => {
    api('GET', '/attendance/roster')
      .then(r => setEmployees(Array.isArray(r) ? r : (r.items || [])))
      .catch(() => setEmployees([]));
  }, []);

  const selectedEmp = employees.find(e => e.id === selectedEmpId) || null;

  const loadHistory = React.useCallback(async (empId) => {
    if (!empId) return;
    setLoading(true);
    try {
      const list = await api('GET', `/employees/${empId}/salaries`);
      setHistory(Array.isArray(list) ? list : []);
    } catch (e) {
      toast('載入失敗：' + (e.detail || ''), 'error');
      setHistory([]);
    } finally { setLoading(false); }
  }, [toast]);

  React.useEffect(() => { loadHistory(selectedEmpId); }, [selectedEmpId, loadHistory]);

  const onSaved = () => {
    setShowAdjust(false);
    setEditingSalary(null);
    loadHistory(selectedEmpId);
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api('DELETE', `/employees/${selectedEmpId}/salaries/${confirmDelete.id}`);
      toast('已刪除', 'ok');
      setConfirmDelete(null);
      loadHistory(selectedEmpId);
    } catch (e) { toast('刪除失敗：' + (e.detail || ''), 'error'); }
  };

  return (
    <>
      <Card padding="0" eyebrow="Salary History" title="薪資歷史" italic="員工合約月薪變動紀錄" right={
        selectedEmp && (
          <Button variant="primary" size="md" icon="plus" onClick={() => { setEditingSalary(null); setShowAdjust(true); }}>
            調薪
          </Button>
        )
      }>
        <div style={{ padding: 18, borderBottom: `1px solid ${HR.ruleSoft}` }}>
          <Field label="選擇員工">
            <select
              value={selectedEmpId || ''}
              onChange={(e) => setSelectedEmpId(parseInt(e.target.value, 10) || null)}
              style={inputStyle()}
            >
              <option value="">— 請選擇員工 —</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>
                  {e.name}{e.emp_code ? ` (${e.emp_code})` : ''}
                  {e.total_salary != null ? ` — 目前 ${fmtMoney(e.total_salary)}` : ' — 尚無薪資'}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {!selectedEmpId ? (
          <EmptyState icon="user" title="請先選擇員工" hint="從上方下拉選擇要查看的員工。" />
        ) : loading ? (
          <SkeletonTable rows={4} />
        ) : history.length === 0 ? (
          <EmptyState
            icon="dollar-sign"
            title="此員工尚無薪資歷史紀錄"
            hint="點右上角「調薪」建立第一筆。"
          />
        ) : (
          <div className="va2-table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <thead>
                <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                  <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>生效日</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>月薪</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>備註</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>建立時間</th>
                  <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, idx) => (
                  <tr key={h.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                    <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xs }}>
                      {h.effective_from}
                      {idx === 0 && (
                        <span style={uiStyle({
                          marginLeft: 8, padding: '2px 6px', fontSize: FontSizes.trading_sm,
                          background: HR.accent, color: 'white', borderRadius: 3,
                        }, {
                          padding: '2px 8px', background: GxT.tint.ok.bg, color: GxT.tint.ok.fg,
                          borderRadius: GxT.radius.chipSm, fontFamily: GxT.font, fontWeight: 700,
                        })}>目前生效</span>
                      )}
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>
                      {fmtMoney(h.total_salary)}
                    </td>
                    <td style={_pyGxUpright({ padding: '13px 16px', color: HR.inkSoft, fontSize: FontSizes.xxs, fontStyle: h.note ? 'italic' : 'normal', fontFamily: HR.serif })}>
                      {h.note || '—'}
                    </td>
                    <td style={{ padding: '13px 16px', color: HR.inkSoft, fontSize: FontSizes.xxs, fontFamily: HR.mono }}>
                      {h.created_at?.slice(0, 10) || '—'}
                    </td>
                    <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                      <Button size="sm" variant="ghost"
                        onClick={() => { setEditingSalary(h); setShowAdjust(true); }}>
                        修正
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => setConfirmDelete(h)}
                        style={{ color: HR.down, marginLeft: 4 }}>
                        刪除
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showAdjust && selectedEmp && (
        <SalaryAdjustModal
          employee={selectedEmp}
          editing={editingSalary}
          onCancel={() => { setShowAdjust(false); setEditingSalary(null); }}
          onSaved={onSaved}
        />
      )}

      {confirmDelete && (
        <Modal
          open onClose={() => setConfirmDelete(null)}
          title="刪除調薪紀錄" subtitle="Confirm Delete" size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirmDelete(null)}>取消</Button>
              <Button variant="primary" onClick={onDelete} style={{ background: HR.down }}>刪除</Button>
            </>
          }
        >
          <div style={{ padding: '8px 0', fontSize: FontSizes.xs }}>
            確定要刪除 <strong style={{ fontFamily: HR.mono }}>
              {confirmDelete.effective_from}</strong> 生效的調薪紀錄
            （月薪 <strong>{fmtMoney(confirmDelete.total_salary)}</strong>）？
            <div style={{ marginTop: 8, color: HR.inkSoft, fontSize: FontSizes.xxs }}>
              此操作會永久刪除該紀錄，已計算過的薪資單不會被自動修改。
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}


/* ─────────────────────  Recurring items tab (固定加減項)  ────────────── */

/* 員工固定加減項主檔（payroll_recurring_items）：
 *   加項 → calculate 時併入引擎 bonus 路徑（進 gross、應稅）；
 *   減項 → 併入 other_deduct 路徑（稅後，不影響投保）。
 * 停用＝設 effective_to（保留歷史軌跡）；金額調整＝停用舊筆＋新增一筆。
 * 刪除＝admin 限定、僅供誤建即刪（DELETE /payroll/recurring-items/{id}）：迄月含當月且不可早於起月，
 * 只在單一月份生效的項目無法用「停用」拿掉，所以保留這個入口。
 * 主檔異動不回溯已鎖定薪資單，需對 open/calculating 期間重新計算才生效。 */

const RECURRING_KIND = {
  allowance: { label: '加項（併入獎金）', tone: 'accent' },
  deduction: { label: '減項（併入其他扣款）', tone: 'warn' },
};

const RECURRING_HINT = '異動後需對 open/calculating 期間重新計算薪資才會生效；金額調整請停用舊筆後新增一筆。';

// 'YYYY-MM'（本地時區），offset 為月份位移：0=當月、1=下個月
function ymWithOffset(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const YM_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

// 生效中＝effective_to 為 null 或 >= 當月（'YYYY-MM' 字串可直接比大小）
function isRecurringActive(item, nowYm) {
  return !item.effective_to || item.effective_to >= nowYm;
}

function NewRecurringItemModal({ employees, defaultEmployeeId, onCancel, onSaved }) {
  const toast = useToast();
  const [employeeId, setEmployeeId] = React.useState(defaultEmployeeId || '');
  const [kind, setKind] = React.useState('allowance');
  const [name, setName] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [effectiveFrom, setEffectiveFrom] = React.useState(ymWithOffset(1)); // 預設下個月生效
  const [remark, setRemark] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    const empId = parseInt(employeeId, 10);
    if (!empId) { toast('請選擇員工', 'error'); return; }
    if (!name.trim()) { toast('名稱必填', 'error'); return; }
    const amt = Math.round(parseFloat(amount));
    if (!(amt > 0)) { toast('金額必填且須大於 0', 'error'); return; }
    if (!YM_RE.test(effectiveFrom)) { toast('生效起須為 YYYY-MM 格式', 'error'); return; }
    setSubmitting(true);
    try {
      await api('POST', '/payroll/recurring-items', {
        employee_id: empId,
        kind,
        name: name.trim(),
        amount: amt,
        effective_from: effectiveFrom,
        remark: remark.trim() || null,
      });
      toast(`已新增固定項「${name.trim()}」（${effectiveFrom} 起生效）`, 'ok');
      onSaved();
    } catch (e) { toast('新增失敗：' + (e.detail || ''), 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onCancel}
      title="新增固定加減項"
      subtitle="New Recurring Item"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '建立中…' : '建立'}</Button>
        </>
      }
    >
      <div className={GxCore.cls('hr-form-grid', 'gx-py-form')} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="員工" required span={2}>
          <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} style={inputStyle()}>
            <option value="">— 請選擇員工 —</option>
            {employees.map(e => (
              <option key={e.id} value={e.id}>{e.name}{e.emp_code ? ` (${e.emp_code})` : ''}</option>
            ))}
          </select>
        </Field>
        <Field label="類型" required>
          <select value={kind} onChange={(e) => setKind(e.target.value)} style={inputStyle()}>
            <option value="allowance">{RECURRING_KIND.allowance.label}</option>
            <option value="deduction">{RECURRING_KIND.deduction.label}</option>
          </select>
        </Field>
        <Field label="金額（每月）" required>
          <input type="number" min={1} step={1} value={amount} onChange={(e) => setAmount(e.target.value)}
            style={inputStyle({ fontFamily: HR.mono, textAlign: 'right' })} />
        </Field>
        <Field label="名稱" hint="後台顯示用，如「固定獎金」「租屋費-XX路」；薪資單上僅呈現併入後合計" required span={2}>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle()} />
        </Field>
        <Field label="生效起（含當月）" required>
          <input type="month" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
            style={inputStyle({ fontFamily: HR.mono })} />
        </Field>
        <Field label="備註">
          <input type="text" value={remark} onChange={(e) => setRemark(e.target.value)} style={inputStyle()} />
        </Field>
      </div>
      <div style={_pyGxBox({
        marginTop: 12, padding: 10, fontSize: FontSizes.xxs, color: HR.inkSoft,
        background: HR.surfaceAlt, borderRadius: 4,
      })}>
        {RECURRING_HINT}
      </div>
    </Modal>
  );
}

function DeactivateRecurringModal({ item, employeeName, onCancel, onSaved }) {
  const toast = useToast();
  const [effectiveTo, setEffectiveTo] = React.useState(ymWithOffset(0)); // 預設當月止
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (!YM_RE.test(effectiveTo)) { toast('迄月須為 YYYY-MM 格式', 'error'); return; }
    if (item.effective_from && effectiveTo < item.effective_from) {
      toast(`迄月不可早於生效起（${item.effective_from}）`, 'error'); return;
    }
    setSubmitting(true);
    try {
      await api('PATCH', `/payroll/recurring-items/${item.id}`, { effective_to: effectiveTo });
      toast(`已停用「${item.name}」（${effectiveTo} 止）`, 'ok');
      onSaved();
    } catch (e) { toast('停用失敗：' + (e.detail || ''), 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onCancel}
      title="停用固定項"
      subtitle="Deactivate Recurring Item"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting} style={{ background: HR.down }}>
            {submitting ? '處理中…' : '確認停用'}
          </Button>
        </>
      }
    >
      <div style={{ marginBottom: 14, fontSize: FontSizes.xs }}>
        確定停用 <strong>{employeeName}</strong> 的
        「<strong>{item.name}</strong>」（{RECURRING_KIND[item.kind]?.label || item.kind}
        · <span style={{ fontFamily: HR.mono }}>{fmtMoney(item.amount)}</span>/月）？
      </div>
      <Field label="生效迄月（含當月，該月仍會計入）" required>
        <input type="month" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)}
          style={inputStyle({ fontFamily: HR.mono })} />
      </Field>
      <div style={_pyGxBox({
        marginTop: 12, padding: 10, fontSize: FontSizes.xxs, color: HR.inkSoft,
        background: HR.surfaceAlt, borderRadius: 4,
      })}>
        {RECURRING_HINT}
      </div>
    </Modal>
  );
}

function RecurringItemsTab() {
  const toast = useToast();
  const [employees, setEmployees] = React.useState([]);
  const [filterEmpId, setFilterEmpId] = React.useState('');   // '' = 全部
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showNew, setShowNew] = React.useState(false);
  const [deactivating, setDeactivating] = React.useState(null);
  const [deleting, setDeleting] = React.useState(null);   // admin 限定：誤建即刪
  const [deleteBusy, setDeleteBusy] = React.useState(false);
  // DELETE API 是 admin 限定（hr 會 403），按鈕也只對 admin 顯示；比照 hr-courses.jsx 用 isAdmin(window._USER)
  const canDelete = (typeof isAdmin === 'function') && isAdmin(window._USER);

  // 員工清單來源比照「薪資歷史」tab：/attendance/roster（HR 可存取，回傳陣列）
  React.useEffect(() => {
    api('GET', '/attendance/roster')
      .then(r => setEmployees(Array.isArray(r) ? r : (r.items || [])))
      .catch(() => setEmployees([]));
  }, []);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const qs = filterEmpId ? `?employee_id=${filterEmpId}` : '';
      const list = await api('GET', `/payroll/recurring-items${qs}`);
      setItems(Array.isArray(list) ? list : (list.items || []));
    } catch (e) {
      toast('載入失敗：' + (e.detail || ''), 'error');
      setItems([]);
    } finally { setLoading(false); }
  }, [filterEmpId, toast]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const nowYm = ymWithOffset(0);
  const empName = (it) => it.employee_name
    || employees.find(e => e.id === it.employee_id)?.name
    || ('#' + it.employee_id);

  const onDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api('DELETE', `/payroll/recurring-items/${deleting.id}`);
      toast(`已刪除「${deleting.name}」；異動後需對 open/calculating 期間重新計算薪資才會生效`, 'ok');
      setDeleting(null);
      await refresh();
    } catch (e) { toast('刪除失敗：' + (e.detail || ''), 'error'); }
    finally { setDeleteBusy(false); }
  };

  return (
    <>
      <Card padding="0" eyebrow="Recurring Items" title="固定加減項" italic={`${items.length} 筆`} right={
        <Button variant="primary" size="md" icon="plus" onClick={() => setShowNew(true)}>新增固定項</Button>
      }>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${HR.ruleSoft}` }}>
          <div style={{ maxWidth: 360 }}>
            <Field label="員工篩選">
              <select value={filterEmpId} onChange={(e) => setFilterEmpId(e.target.value)} style={inputStyle()}>
                <option value="">全部員工</option>
                {employees.map(e => (
                  <option key={e.id} value={e.id}>{e.name}{e.emp_code ? ` (${e.emp_code})` : ''}</option>
                ))}
              </select>
            </Field>
          </div>
          <div style={_pyGxBox({
            marginTop: 10, padding: 10, fontSize: FontSizes.xxs, color: HR.inkSoft,
            background: HR.surfaceAlt, borderRadius: 4,
          })}>
            {RECURRING_HINT}
          </div>
        </div>

        {loading ? (
          <SkeletonTable rows={4} />
        ) : items.length === 0 ? (
          <EmptyState icon="dollar-sign" title="尚無固定加減項" hint="點右上角「新增固定項」建立第一筆。" />
        ) : (
          <div className="va2-table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <thead>
                <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                  <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>員工</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>類型</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>名稱</th>
                  <th style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 500 }}>金額/月</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>生效起迄</th>
                  <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>狀態</th>
                  <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => {
                  const active = isRecurringActive(it, nowYm);
                  const kindMeta = RECURRING_KIND[it.kind] || { label: it.kind, tone: 'neutral' };
                  return (
                    <tr key={it.id} style={{ borderTop: `1px solid ${HR.ruleSoft}`, opacity: active ? 1 : 0.55 }}>
                      <td style={{ padding: '13px 16px 13px 24px', fontWeight: 500 }}>{empName(it)}</td>
                      <td style={{ padding: '13px 16px' }}>
                        <Pill tone={kindMeta.tone} dot>{kindMeta.label}</Pill>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        {it.name}
                        {it.remark && (
                          <div style={_pyGxUpright({ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontStyle: 'italic', marginTop: 2 })}>
                            {it.remark}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>
                        {fmtMoney(it.amount)}
                      </td>
                      <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                        {it.effective_from} ~ {it.effective_to || '（持續）'}
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <Pill tone={active ? 'success' : 'neutral'} dot>{active ? '生效中' : '已停用'}</Pill>
                      </td>
                      <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                        {active && (
                          <Button size="sm" variant="ghost" style={{ color: HR.down }}
                            onClick={() => setDeactivating(it)}>
                            停用
                          </Button>
                        )}
                        {canDelete && (
                          <Button size="sm" variant="ghost" style={{ color: HR.down, marginLeft: 4 }}
                            title="僅供誤建即刪；正常停用請設迄月"
                            onClick={() => setDeleting(it)}>
                            刪除
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showNew && (
        <NewRecurringItemModal
          employees={employees}
          defaultEmployeeId={filterEmpId}
          onCancel={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); refresh(); }}
        />
      )}
      {deactivating && (
        <DeactivateRecurringModal
          item={deactivating}
          employeeName={empName(deactivating)}
          onCancel={() => setDeactivating(null)}
          onSaved={() => { setDeactivating(null); refresh(); }}
        />
      )}
      {canDelete && (
        <ConfirmDialog
          open={!!deleting}
          title="刪除固定加減項"
          message={deleting
            ? `確定要刪除 ${empName(deleting)} 的「${deleting.name}」（${RECURRING_KIND[deleting.kind]?.label || deleting.kind}`
              + ` · ${fmtMoney(deleting.amount)}/月，${deleting.effective_from} ~ ${deleting.effective_to || '持續'}）？`
              + '\n\n刪除只供誤建即刪，紀錄會永久移除、不留歷史軌跡；正常停用請改用「停用」設迄月。'
              + '已鎖定的薪資單不受影響，open/calculating 期間需重新計算才會反映。'
            : ''}
          danger
          busy={deleteBusy}
          onCancel={() => setDeleting(null)}
          onConfirm={onDelete}
        />
      )}
    </>
  );
}


function PayrollPage() {
  const [user, setUser] = React.useState(null);
  const [periods, setPeriods] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedPeriod, setSelectedPeriod] = React.useState(null);
  const [showNewPeriod, setShowNewPeriod] = React.useState(false);
  const [tab, setTab] = React.useState('me');

  // Capability gates — backend role mapping: hr=write+approve, finance=pay, admin=all
  const canEdit = hasAnyCapability(user, 'payroll.write', 'payroll.approve');
  const canPay = hasCapability(user, 'payroll.pay');
  const canSeeAll = hasCapability(user, 'payroll.read.all');  // 看「薪資期間」分頁的入口

  const refresh = React.useCallback(async () => {
    const list = await api('GET', '/payroll/periods').catch(() => []);
    const arr = Array.isArray(list) ? list : [];
    setPeriods(arr);
    setSelectedPeriod(prev => prev ? (arr.find(p => p.id === prev.id) || null) : null);
  }, []);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      const u = await api('GET', '/auth/me').catch(() => null);
      if (!alive) return;
      setUser(u);
      if (hasCapability(u, 'payroll.read.all')) {
        await refresh();
        setTab('periods');
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [refresh]);

  if (loading) {
    return (
      <>
        <PageHeader eyebrow="財務 · Payroll" title="薪資" italic="Compensation" />
        <SkeletonTable rows={6} />
      </>
    );
  }

  const tabs = [
    { key: 'me', label: '我的薪資', icon: 'dollar-sign' },
    ...(canSeeAll ? [{ key: 'periods', label: '薪資期間', icon: 'calendar' }] : []),
    ...(canSeeAll ? [{ key: 'breakdown', label: '薪資拆解', icon: 'file-text' }] : []),
    ...(canEdit ? [{ key: 'salaries', label: '薪資歷史', icon: 'trending-up' }] : []),
    // 固定加減項主檔：API 為 hr/admin 限定 → 用 canEdit（payroll.write/approve）gate，
    // 不用 canSeeAll（finance 也持 payroll.read.all，但無此 API 權限）。
    ...(canEdit ? [{ key: 'recurring', label: '固定加減項', icon: 'refresh' }] : []),
  ];

  const BreakdownTab = window.PayrollBreakdownTab;

  return (
    <>
      <PageHeader eyebrow="財務 · Payroll" title="薪資" italic={canEdit ? 'Payroll Console' : 'My Compensation'} />
      <Tabs tabs={tabs} value={tab} onChange={(k) => { setTab(k); setSelectedPeriod(null); }} />

      {tab === 'me' && <MyPayslipsTab />}

      {tab === 'breakdown' && canSeeAll && BreakdownTab && <BreakdownTab />}

      {tab === 'salaries' && <SalariesTab />}

      {tab === 'recurring' && canEdit && <RecurringItemsTab />}

      {tab === 'periods' && (
        selectedPeriod ? (
          <PeriodDetail
            period={selectedPeriod}
            isHR={canEdit}
            isFinance={canPay}
            onBack={() => setSelectedPeriod(null)}
            onChanged={refresh}
          />
        ) : (
          <PeriodsList
            periods={periods}
            onSelect={setSelectedPeriod}
            onNew={() => setShowNewPeriod(true)}
            isHR={canEdit}
            onChanged={refresh}
          />
        )
      )}

      {showNewPeriod && (
        <NewPeriodForm
          onCancel={() => setShowNewPeriod(false)}
          onSaved={() => { setShowNewPeriod(false); refresh(); }}
        />
      )}
    </>
  );
}

// Expressive (GX) page CSS — only inserted under <html data-ui-shell="expressive"> (no-op in Bento;
// router fallback removes every style[id^="gx-css-"]). On real phone widths (GX does not force the
// 1280 viewport) index.html's `.hr-form-grid{1fr}` still lets inline `gridColumn: span N` fields open
// implicit columns; the GX-only class collapses every field to the single column. Layout only.
GxCore.injectCss('gx-css-p-payroll', String.raw`
@media (max-width:767px){
html[data-ui-shell="expressive"] .gx-py-form > *{grid-column:1 / -1!important}
}
`);

window.PayrollPage = PayrollPage;
